/**
 * Football geometry and rigid-body contact (owner: ball team). The ball is a solid ellipse with semi-axes
 * BALL_HALF_LENGTH (along `angle`) × BALL_HALF_HEIGHT, unit mass, inertia (a² + b²) / 4. Only + - * / sqrt and
 * dmath, so results are bit-identical everywhere.
 *
 * Allocation-free by construction: the hot helpers take objects and Smis only and exchange doubles through the
 * module scratch objects below (`rot`, `dir`, `support`, `imp`). A JS engine boxes a double crossing a call
 * boundary it did not inline; this calling convention makes that impossible, so the ball never allocates per tick
 * whatever the optimizer decides.
 */
import { BALL_HALF_HEIGHT, BALL_HALF_LENGTH, PI, dcos, dsin, dsinCosInto } from '@hf/shared';
import type { BallSim } from '../state';

const A2 = BALL_HALF_LENGTH * BALL_HALF_LENGTH;
const B2 = BALL_HALF_HEIGHT * BALL_HALF_HEIGHT;
/** 1 / moment of inertia for a unit-mass solid ellipse. */
export const BALL_INV_INERTIA = 4 / (A2 + B2);

/** cos / sin of the ball angle (set by `setRotation`). */
export const rot = { c: 0.5, s: 0.5 };
/** Query direction for `supportAlongDir` (unit vector). */
export const dir = { x: 0.5, y: -0.5 };
/** Support result: the ellipse point furthest along `dir`, relative to the centre, and the extent along `dir`. */
export const support = { x: 0.5, y: 0.5, h: 0.5 };
/**
 * Impulse inputs/outputs for `ballImpulse`: surface normal (nx, ny) from the surface to the ball, contact offset
 * (rx, ry) from the ball centre, restitution `e`, friction `mu`; `approach` = closing speed (0 if separating).
 */
export const imp = { nx: 0.5, ny: 0.5, rx: 0.5, ry: 0.5, e: 0.5, mu: 0.5, approach: 0.5 };

/** rot ← (cos, sin) of the ball's angle. */
export const setRotation = (b: BallSim): void => {
  // into `rot` directly (dcos / dsin return doubles: boxed on this per-tick path when not inlined)
  dsinCosInto(rot, b.angle);
};

/**
 * Support point of the ellipse (rotated by `rot`) along unit `dir`: p = M u / sqrt(uᵀ M u) with
 * M = R diag(a², b²) Rᵀ. Writes `support`.
 */
export const supportAlongDir = (): void => {
  const c = rot.c;
  const sn = rot.s;
  const ux = dir.x;
  const uy = dir.y;
  const mxx = A2 * c * c + B2 * sn * sn;
  const myy = A2 * sn * sn + B2 * c * c;
  const mxy = (A2 - B2) * sn * c;
  const mx = mxx * ux + mxy * uy;
  const my = mxy * ux + myy * uy;
  const h = Math.sqrt(ux * mx + uy * my);
  support.h = h;
  support.x = mx / h;
  support.y = my / h;
};

/** Convenience for cold paths and tests: support of a ball at `angle` along (ux, uy); returns the extent. */
export const ballSupport = (angle: number, ux: number, uy: number): number => {
  rot.c = dcos(angle);
  rot.s = dsin(angle);
  dir.x = ux;
  dir.y = uy;
  supportAlongDir();
  return support.h;
};

/** Half the vertical extent of the ball at `angle` (its lowest point is y − this). Cold paths / tests. */
export const ballHalfHeightAt = (angle: number): number => {
  const c = dcos(angle);
  const sn = dsin(angle);
  return Math.sqrt(A2 * sn * sn + B2 * c * c);
};

/** Wrap an angle difference into [-π/2, π/2] (the ellipse is symmetric under a half turn). */
export const wrapHalfTurn = (d: number): number => d - PI * Math.round(d / PI);

/**
 * Rigid-body impulse of the ball against a static surface, inputs in `imp`. Only applies when the contact point
 * is approaching the surface; Coulomb friction couples the tangential slip into spin (angle-dependent bounce).
 */
export const ballImpulse = (b: BallSim): void => {
  const nx = imp.nx;
  const ny = imp.ny;
  const rx = imp.rx;
  const ry = imp.ry;
  const vcx = b.vx - b.angVel * ry;
  const vcy = b.vy + b.angVel * rx;
  const vn = vcx * nx + vcy * ny;
  if (vn >= 0) {
    imp.approach = 0;
    return;
  }
  const rn = rx * ny - ry * nx;
  const jn = (-(1 + imp.e) * vn) / (1 + rn * rn * BALL_INV_INERTIA);
  const tx = -ny;
  const ty = nx;
  const vt = vcx * tx + vcy * ty;
  const rt = rx * ty - ry * tx;
  let jt = -vt / (1 + rt * rt * BALL_INV_INERTIA);
  const maxF = imp.mu * jn;
  if (jt > maxF) jt = maxF;
  else if (jt < -maxF) jt = -maxF;
  b.vx += jn * nx + jt * tx;
  b.vy += jn * ny + jt * ty;
  b.angVel += (rn * jn + rt * jt) * BALL_INV_INERTIA;
  imp.approach = -vn;
};
